/**
 * SSOT for packages/profile bank-transfer save → LDS proposal guard → navigation.
 * Does not change pricing, payment capture, or LDS XML — routing/UX only.
 */

import {
  getPackageOrderProfilePath,
  getPackageOrderSuccessPath,
} from "./ldsOrderStatus";
import {
  resolveAgentLdsFlightsHotelPostOrderPath,
  shouldUseAgentDashboardLdsPaymentNav,
} from "./orders/resolveAgentLdsFlightsHotelPaymentNav";
import { resolveLdsPaymentPageBankTransferSuccessPath } from "./orders/resolveLdsPaymentPageBankTransfer";

export const PACKAGE_BANK_TRANSFER_PENDING_MESSAGE_HE =
  "הבקשה נשמרה וממתינה להעברה בנקאית";

/**
 * @param {unknown} saveResponse axios response or order row
 * @returns {object|null}
 */
export function extractLdsOrderFromSaveResponse(saveResponse) {
  if (!saveResponse || typeof saveResponse !== "object") return null;
  const data = saveResponse.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.id != null || data.ID != null) return data;
  }
  if (saveResponse.id != null || saveResponse.ID != null) return saveResponse;
  return null;
}

/**
 * @param {string} phase
 * @param {Record<string, unknown>} [payload]
 */
export function logBankTransferFlowTrace(phase, payload = {}) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV !== true) return;
    // eslint-disable-next-line no-console
    console.info(`[${phase}]`, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object} payload
 */
export function logBankTransferButtonClickTrace(payload = {}) {
  logBankTransferFlowTrace("BANK_TRANSFER_BUTTON_CLICK", payload);
}

/**
 * @param {object} payload
 */
export function logBankTransferHandlerEnterTrace(payload = {}) {
  logBankTransferFlowTrace("BANK_TRANSFER_HANDLER_ENTER", payload);
}

/**
 * @param {object} payload
 */
export function logBankTransferBeforeSaveTrace(payload = {}) {
  logBankTransferFlowTrace("BANK_TRANSFER_BEFORE_SAVE", payload);
}

/**
 * @param {object} payload
 */
export function logBankTransferAfterSaveTrace(payload = {}) {
  logBankTransferFlowTrace("BANK_TRANSFER_AFTER_SAVE", payload);
}

/**
 * @param {object} payload
 */
export function logBankTransferHandlerSkippedTrace(payload = {}) {
  logBankTransferFlowTrace("BANK_TRANSFER_HANDLER_SKIPPED", payload);
}

function _proposalStatusFromOrder(order) {
  if (!order || typeof order !== "object") return null;
  return (
    order.proposal_status ??
    order.order_response?.proposal_status ??
    order.order_response?.status ??
    order.order_response?.opStatus?.status ??
    null
  );
}

/**
 * Resolve post-save navigation target for bank transfer on packages/profile.
 * @param {{
 *   order?: object|null,
 *   nav?: { allowSuccessRoute?: boolean },
 *   agentPaymentNavCtx?: object,
 * }} input
 * @returns {{ targetPath: string|null, kind: string }}
 */
export function resolvePackageProfileBankTransferNavigationTarget(input = {}) {
  const { order = null, nav = {}, agentPaymentNavCtx = {} } = input;
  const oid = order?.id ?? order?.ID ?? null;
  if (oid == null || String(oid).trim() === "") {
    return { targetPath: null, kind: "none" };
  }

  if (nav.allowSuccessRoute) {
    return {
      targetPath: getPackageOrderSuccessPath(oid),
      kind: "package_success_route",
    };
  }

  if (shouldUseAgentDashboardLdsPaymentNav(agentPaymentNavCtx)) {
    return {
      targetPath: resolveLdsPaymentPageBankTransferSuccessPath(oid, {
        preferDashboardSuccess: true,
      }),
      kind: "dashboard_bank_success",
    };
  }

  const paymentPath = resolveAgentLdsFlightsHotelPostOrderPath(
    oid,
    order,
    agentPaymentNavCtx
  );
  if (paymentPath) {
    return { targetPath: paymentPath, kind: "agent_payment" };
  }

  return {
    targetPath: getPackageOrderProfilePath(oid),
    kind: "profile_pending",
  };
}

/**
 * @param {object} deps
 * @returns {{ ok: boolean, navigated?: boolean, targetPath?: string|null, order?: object|null, nav?: object }}
 */
export function executePackageProfileBankTransferAfterSave(deps = {}) {
  const {
    saveResponse,
    source = "unknown",
    navigate,
    messageApi = null,
    ModalApi = null,
    agentPaymentNavCtx = {},
    scrollToErrorTarget = null,
    armSuppression = null,
    clearSuppression = null,
    guardSuccessNav = null,
    resolveLdsErrorAction: resolveLdsErrorActionFn = null,
    shouldNavigateToLdsSuccess: shouldNavigateToLdsSuccessFn = null,
    buildAgentPaymentNavState = null,
    isPaymentSuccess = false,
    onComplete = null,
  } = deps;

  logBankTransferFlowTrace("BANK_TRANSFER_FLOW_START", { source });

  const order = extractLdsOrderFromSaveResponse(saveResponse);
  const oid = order?.id ?? order?.ID ?? null;
  const profilePath = oid ? getPackageOrderProfilePath(oid) : null;
  const proposalStatus = _proposalStatusFromOrder(order);

  logBankTransferFlowTrace("BANK_TRANSFER_PROPOSAL_START", {
    source,
    orderId: oid,
    proposalStatus,
  });

  if (typeof shouldNavigateToLdsSuccessFn !== "function") {
    onComplete?.({ ok: false });
    return { ok: false, order };
  }

  const nav = shouldNavigateToLdsSuccessFn({
    source,
    axiosResponse: saveResponse,
    order,
    targetPath: profilePath,
  });

  if (!nav.continueFlow) {
    logBankTransferFlowTrace("BANK_TRANSFER_PROPOSAL_FAILED", {
      source,
      orderId: oid,
      reason: nav.reason ?? null,
      decision: nav.decision ?? null,
      proposalStatus,
    });
    armSuppression?.(nav.reason || nav.decision || "bank_transfer_guard_block");
    onComplete?.({ ok: false });
    if (typeof resolveLdsErrorActionFn === "function" && ModalApi) {
      const a = nav.ldsErrorAction || resolveLdsErrorActionFn({ order: order || {} });
      const showModal = a.severity === "warning" ? ModalApi.warning : ModalApi.error;
      showModal({
        title: a.title,
        content: a.message,
        okText: a.actionLabel,
        onOk:
          typeof scrollToErrorTarget === "function"
            ? () => scrollToErrorTarget(a.targetSection)
            : undefined,
        onCancel: () => {},
      });
    }
    return { ok: false, nav, order };
  }

  logBankTransferFlowTrace("BANK_TRANSFER_PROPOSAL_SUCCESS", {
    source,
    orderId: oid,
    proposalStatus,
    completionState: nav.completionState ?? null,
  });

  onComplete?.({ ok: true });

  const { targetPath, kind } = resolvePackageProfileBankTransferNavigationTarget({
    order,
    nav,
    agentPaymentNavCtx,
  });

  logBankTransferFlowTrace("BANK_TRANSFER_NAVIGATION_TARGET", {
    source,
    targetPath,
    kind,
    orderId: oid,
  });

  if (!targetPath || typeof navigate !== "function") {
    messageApi?.success?.(PACKAGE_BANK_TRANSFER_PENDING_MESSAGE_HE);
    return { ok: true, navigated: false, order, nav };
  }

  if (nav.allowSuccessRoute && typeof guardSuccessNav === "function") {
    clearSuppression?.();
    guardSuccessNav({
      source,
      targetPath,
      isNavigatingToSuccess: false,
      isPaymentSuccess,
      ourSavedLDSOrderId: oid,
      execNavigate: () => navigate(targetPath, { replace: true }),
    });
    return { ok: true, navigated: true, targetPath, order, nav };
  }

  clearSuppression?.();
  if (kind === "agent_payment" && typeof buildAgentPaymentNavState === "function") {
    navigate(targetPath, {
      replace: true,
      state: buildAgentPaymentNavState(oid),
    });
  } else {
    navigate(targetPath, { replace: true });
  }

  if (
    kind === "profile_pending" ||
    kind === "dashboard_bank_success" ||
    kind === "agent_payment"
  ) {
    messageApi?.success?.(PACKAGE_BANK_TRANSFER_PENDING_MESSAGE_HE);
  }

  return { ok: true, navigated: true, targetPath, order, nav };
}
